// src/components/templates/CatalogTemplate.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Car, Search, Filter, Phone, MessageCircle, Eye, Heart,
  Fuel, Gauge, Settings2, Calendar, MapPin, Star,
  Grid, List, SlidersHorizontal, Share2, ChevronLeft, ChevronRight,
  ExternalLink, X, RotateCcw
} from 'lucide-react';
import { catalogService } from '../../services/catalogService';

const adjustBrightness = (hex, percent) => {
  if (!hex) return '#0795ff'; // fallback
  
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  
  const adjustColor = (color) => {
    const adjusted = Math.round(color + (color * percent / 100));
    return Math.max(0, Math.min(255, adjusted));
  };
  
  const toHex = (num) => num.toString(16).padStart(2, '0');
  return `#${toHex(adjustColor(r))}${toHex(adjustColor(g))}${toHex(adjustColor(b))}`;
};

export const EnhancedCatalogTemplate = ({ bookId, book, messages, bookData, styling }) => {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: 'all',
    priceRange: 'all',
    brand: 'all',
    status: 'active'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);

  // Fetch items using catalogService
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const itemsData = await catalogService.getItems(bookId);
        setItems(itemsData);
        setFilteredItems(itemsData);
      } catch (error) {
        console.error('Error fetching items:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, [bookId]);

  // Apply filters and search using catalogService
  useEffect(() => {
    const applyFilters = async () => {
      try {
        const filtered = await catalogService.searchItems(bookId, searchTerm, filters);
        setFilteredItems(filtered);
      } catch (error) {
        console.error('Error filtering items:', error);
        setFilteredItems(items);
      }
    };

    applyFilters();
  }, [items, searchTerm, filters, bookId]);

  useEffect(() => {
    if (!book) return;
    
    const root = document.documentElement;
    
    // Use the same property access pattern as your existing component
    const primaryColor = book?.styling?.primaryColor || '#0795ff';
    const backgroundColor = book?.styling?.backgroundColor || book?.backgroundColor || '#f8fafc';
    const headerColor = book?.styling?.headerColor || book?.headerColor || '#ffffff';
    
    root.style.setProperty('--catalog-primary', primaryColor);
    root.style.setProperty('--book-primary', primaryColor);
    root.style.setProperty('--catalog-background', backgroundColor);
    root.style.setProperty('--catalog-header', headerColor);
    
    document.body.style.backgroundColor = backgroundColor;

    // Cleanup on unmount
    return () => {
      root.style.removeProperty('--catalog-primary');
      root.style.removeProperty('--catalog-background'); 
      root.style.removeProperty('--catalog-header');
      root.style.removeProperty('--book-primary');
      document.body.style.backgroundColor = '';
    };
  }, [book]);

  const handleItemClick = async (item) => {
    // Track view
    await catalogService.trackView(bookId, item.id);

    const enhancedItem = {
      ...item,
      images: item.images || (item.imageUrl ? [item.imageUrl] : []),
      videos: item.videos || []
    };

    if (bookData.showItemsInModal !== false) { // Default true
      setSelectedItem(item);
      setShowItemModal(true);
    }
  };

  const handleContact = async (method, contactInfo) => {
    if (selectedItem) {
      await catalogService.trackContact(bookId, selectedItem.id, method);
    }

    switch (method) {
      case 'whatsapp':
        const message = selectedItem ? 
          `Hi! I'm interested in ${selectedItem.name}${selectedItem.price ? ` - ${catalogService.formatPrice(selectedItem.price)}` : ''}. Please provide more details.` :
          `Hi! I'm interested in your products. Please provide more details.`;
        const whatsappUrl = `https://wa.me/${contactInfo}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
        break;
      case 'phone':
        window.location.href = `tel:${contactInfo}`;
        break;
      case 'email':
        const subject = selectedItem ? `Inquiry about ${selectedItem.name}` : 'Product Inquiry';
        const body = selectedItem ? 
          `Hi,\n\nI'm interested in ${selectedItem.name}${selectedItem.price ? ` priced at ${catalogService.formatPrice(selectedItem.price)}` : ''}.\n\nPlease provide more details.\n\nThank you!` :
          `Hi,\n\nI'm interested in your products.\n\nPlease provide more details.\n\nThank you!`;
        window.location.href = `mailto:${contactInfo}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        break;
    }
  };

  const getUniqueValues = (field) => {
    return catalogService.getUniqueValues(items, field);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div 
      className="min-h-screen helvetica-mode"
      style={{ 
        styling
      }}
    >
      {/* Apply colors with safe access - matching your component pattern */}
      <style>{`
        .catalog-button-primary {
          background: linear-gradient(135deg, ${book?.styling?.primaryColor || '#0795ff'}, ${adjustBrightness(book?.styling?.primaryColor || '#0795ff', -10)});
          color: white;
        }
        .catalog-accent {
          color: ${book?.styling?.primaryColor || '#0795ff'};
        }
        .catalog-border {
          border-color: ${book?.styling?.primaryColor || '#0795ff'};
        }
        .catalog-bg-primary {
          background-color: ${book?.styling?.primaryColor || '#0795ff'};
        }
      `}</style>

      {/* Cover Image */}
      {bookData.coverImageUrl && (
        <div className="relative h-64 md:h-80 bg-gray-200">
          <img
            src={bookData.coverImageUrl}
            alt={`${bookData.botName} Cover`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
            <div className="text-center text-white">
              <h1 className="text-4xl md:text-6xl font-bold mb-4">
                {bookData.botName}
              </h1>
              {bookData.description && (
                <p className="text-xl md:text-2xl opacity-90">
                  {bookData.description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <CatalogHeader 
        bookData={bookData} 
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        resultCount={filteredItems.length}
        totalCount={items.length}
        styling={{
          headerColor: book?.styling?.headerColor || book?.headerColor || '#ffffff',
          primaryColor: book?.styling?.primaryColor || '#0795ff',
          backgroundColor: book?.styling?.backgroundColor || book?.backgroundColor || '#f8fafc',
          fontFamily: book?.styling?.fontFamily || 'Inter, sans-serif'
        }}
      />

      {/* Filters Panel */}
      {showFilters && (
        <FilterPanel
          filters={filters}
          onFiltersChange={setFilters}
          items={items}
          getUniqueValues={getUniqueValues}
        />
      )}

      {/* Items Grid/List */}
      <div className="container mx-auto px-2 py-6">
        {filteredItems.length === 0 ? (
          <EmptyState searchTerm={searchTerm} />
        ) : (
          <ItemGrid 
            items={filteredItems}
            viewMode={viewMode}
            bookData={bookData}
            onItemClick={handleItemClick}
            onContact={handleContact}
            gridColumns={bookData.gridColumns || 3}
          />
        )}
      </div>

      {/* Item Detail Modal */}
      {showItemModal && selectedItem && (
        <ItemDetailModal
          item={selectedItem}
          catalog={bookData}
          onClose={() => {
            setShowItemModal(false);
            setSelectedItem(null);
          }}
          onContact={handleContact}
        />
      )}
    </div>
  );
};

// Header Component
const CatalogHeader = ({ 
  bookData, styling, searchTerm, onSearchChange, totalItems,
  showFilters, onToggleFilters, viewMode, onViewModeChange, resultCount 
}) => (
  <div 
    className="sticky top-0 z-50 shadow-sm border-b"
    style={{ backgroundColor: styling?.headerColor || '#ffffff' }}
  >
    <div className="container mx-auto px-4 py-2">
      {/* Brand Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-4">
          {bookData.logoUrl && (
            <img 
              src={bookData.logoUrl} 
              alt={bookData.botName}
              className="h-10 w-auto object-contain"
            />
          )}
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-gray-900">
                {bookData.botName}
              </h1>
              {/* Item Count Flag */}
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
                {resultCount || 0} Items
              </div>
              
            </div>
            <p className="text-gray-600 text-sm">
              {bookData.description || bookData.botTask || 'Product Catalog'}
            </p>
          </div>
        </div>
        
        {/* Contact Info */}
        <div className="hidden md:flex items-center gap-4">
          <div className="text-right">
            <p className="font-semibold text-gray-900">
              {bookData.companyName || bookData.botName}
            </p>
            <p className="text-sm text-gray-600">
              {bookData.contactNumber || bookData.whatsappNumber}
            </p>
          </div>
        </div>
      </div>

      {/* Search and Controls */}
      <div className="flex flex-row gap-4 items-center">
        {/* Search Bar */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search products, brands, or categories..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={onToggleFilters}
            className={`px-4 py-2 text-sm rounded-lg border font-medium transition-colors ${
              showFilters
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Filter className="h-4 w-4 mr-2 inline" />
            <span className="hidden sm:inline">Filters</span>
          </button>

          {/* View Toggle */}
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => onViewModeChange('grid')}
              className={`p-2 ${
                viewMode === 'grid' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => onViewModeChange('list')}
              className={`p-2 ${
                viewMode === 'list' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Filter Panel Component
const FilterPanel = ({ filters, onFiltersChange, items, getUniqueValues }) => {
  const updateFilter = (key, value) => {
    onFiltersChange(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetFilters = () => {
    onFiltersChange({
      category: 'all',
      priceRange: 'all',
      brand: 'all',
      status: 'active'
    });
  };

  const categories = getUniqueValues('category');
  const brands = getUniqueValues('brand');

  const priceRanges = [
    { label: 'All Prices', value: 'all' },
    { label: 'Under R 200k', value: '0-200000' },
    { label: 'R 200k - R 500k', value: '200000-500000' },
    { label: 'R 500k - R 1M', value: '500000-1000000' },
    { label: 'Over R 1M', value: '1000000-999999999' }
  ];

  return (
    <div className="bg-white border-b shadow-sm">
      
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Filter Options</h3>
          <button
            onClick={resetFilters}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            Reset All
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              value={filters.category}
              onChange={(e) => updateFilter('category', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Price Range Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price Range
            </label>
            <select
              value={filters.priceRange}
              onChange={(e) => updateFilter('priceRange', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {priceRanges.map(range => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>

          {/* Brand Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Brand
            </label>
            <select
              value={filters.brand}
              onChange={(e) => updateFilter('brand', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Brands</option>
              {brands.map(brand => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => updateFilter('status', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Items</option>
              <option value="active">Available</option>
              <option value="sold">Sold</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

// Item Grid Component
const ItemGrid = ({ items, viewMode, bookData, onItemClick, onContact, gridColumns }) => {
  const getGridClass = () => {
    if (viewMode === 'list') return 'space-y-4';
    
    const columnClasses = {
      1: 'grid-cols-1',
      2: 'grid-cols-1 md:grid-cols-2',
      3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
      4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
      5: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5',
      6: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6'
    };
    
    return `grid ${columnClasses[gridColumns] || columnClasses[3]} gap-6`;
  };

  return (
    <div className={getGridClass()}>
      {items.map((item) => (
        <ItemCard
          key={item.id}
          item={item}
          catalog={bookData}
          viewMode={viewMode}
          onItemClick={onItemClick}
          onContact={onContact}
        />
      ))}
    </div>
  );
};

// Item Card Component
const ItemCard = ({ item, catalog, viewMode, onItemClick, onContact }) => {
  const [liked, setLiked] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  const images = Array.isArray(item.images) ? item.images : [];
  const hasImages = images.length > 0;
  const primaryImage = item.imageUrl || images[0];
  const displayImage = hasImages
    ? (images[currentImageIndex] || primaryImage)
    : primaryImage;

  const getCategoryIcon = (category) => {
    const icons = {
      'automotive': '🚗',
      'real-estate': '🏠',
      'retail': '🛍️',
      'food': '🍽️',
      'services': '🔧',
      'default': '📦'
    };
    return icons[category] || icons.default;
  };

  const handleShare = async () => {
    const shareData = {
      title: item.name,
      text: `Check out ${item.name}${item.price ? ` - ${catalogService.formatPrice(item.price)}` : ''}`,
      url: window.location.href
    };

    if (navigator.share) {
      await navigator.share(shareData);
    } else {
      navigator.clipboard.writeText(`${shareData.title} - ${shareData.url}`);
      alert('Link copied to clipboard!');
    }
  };

  useEffect(() => {
    setCurrentImageIndex(0);
    setImageLoaded(false);
    setImageError(false);
  }, [item.id, images.length]);

  const handlePrevImage = (e) => {
    e.stopPropagation();
    if (images.length < 2) return;
    setImageLoaded(false);
    setImageError(false);
    setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    if (images.length < 2) return;
    setImageLoaded(false);
    setImageError(false);
    setCurrentImageIndex(prev => (prev + 1) % images.length);
  };

  if (viewMode === 'list') {
    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
           onClick={() => onItemClick(item)}>
        <div className="flex group">
          {/* Image */}
          <div className="w-64 h-48 bg-gray-200 flex-shrink-0 relative overflow-hidden">
            {!imageError && displayImage ? (
              <>
                <img
                  src={displayImage}
                  alt={item.name}
                  className={`w-full h-full object-cover ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageError(true)}
                />
                {!imageLoaded && (
                  <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-4xl">
                {getCategoryIcon(item.category)}
              </div>
            )}

            {images.length > 1 && (
              <>
                <button
                  onClick={handlePrevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-white/80 text-gray-700 hover:bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Previous image"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={handleNextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-white/80 text-gray-700 hover:bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Next image"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </>
            )}

            {images.length > 0 && (
              <div className="absolute bottom-2 right-2 bg-black/60 text-white px-2 py-0.5 rounded-full text-[10px] backdrop-blur-sm">
                {currentImageIndex + 1} / {images.length}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 p-6 flex justify-between">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {item.name}
              </h3>
              
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <span className="capitalize">{item.category}</span>
                {item.location && (
                  <>
                    <span>•</span>
                    <MapPin className="h-4 w-4" />
                    <span>{item.location}</span>
                  </>
                )}
              </div>

              {item.description && (
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {item.description}
                </p>
              )}
            </div>

            {/* Price and Actions */}
            <div className="text-right ml-6">
              {item.price ? (
                <p className="text-2xl font-bold text-red-700 mb-4">
                  {catalogService.formatPrice(item.price)}
                </p>
              ) : (
                <p className="text-lg font-semibold text-gray-900 mb-4">
                  Contact for Price
                </p>
              )}

              <div className="flex flex-col gap-2">
                {catalog.whatsappNumber && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onContact('whatsapp', catalog.whatsappNumber);
                    }}
                    className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <MessageCircle className="h-4 w-4" />
                    WhatsApp
                  </button>
                )}
                
                {catalog.contactNumber && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onContact('phone', catalog.contactNumber);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <Phone className="h-4 w-4" />
                    Call
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group"
         onClick={() => onItemClick(item)}>
      {/* Image Section */}
      <div className="relative h-64 bg-gray-200">
        {!imageError && displayImage ? (
          <>
            <img
              src={displayImage}
              alt={item.name}
              className={`w-full h-full object-cover ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 text-6xl">
            {getCategoryIcon(item.category)}
          </div>
        )}

        {images.length > 1 && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 text-gray-700 hover:bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
              title="Previous image"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 text-gray-700 hover:bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
              title="Next image"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </>
        )}

        {images.length > 0 && (
          <div className="absolute bottom-3 right-3 bg-black/60 text-white px-2 py-1 rounded-full text-xs backdrop-blur-sm">
            {currentImageIndex + 1} / {images.length}
          </div>
        )}

        {/* Overlay Actions */}
        <div className="absolute top-4 right-4 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLiked(!liked);
            }}
            className={`p-2 rounded-full backdrop-blur-sm transition-colors ${
              liked 
                ? 'bg-red-500 text-white' 
                : 'bg-white/80 text-gray-700 hover:bg-white'
            }`}
          >
            <Heart className="h-4 w-4" fill={liked ? 'currentColor' : 'none'} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleShare();
            }}
            className="p-2 rounded-full bg-white/80 text-gray-700 hover:bg-white backdrop-blur-sm transition-colors"
          >
            <Share2 className="h-4 w-4" />
          </button>
        </div>

        {/* Primary Contact Button */}
        {catalog.whatsappNumber && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onContact('whatsapp', catalog.whatsappNumber);
            }}
            className="absolute top-4 left-4 bg-green-500 hover:bg-green-600 text-white p-2 rounded-full transition-colors shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <MessageCircle className="h-5 w-5" />
          </button>
        )}

        {/* Featured Badge */}
        {item.featured && (
          <div className="absolute bottom-4 left-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
            <Star className="h-3 w-3" fill="currentColor" />
            FEATURED
          </div>
        )}

        {/* Status Badge */}
        {item.status === 'sold' && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <span className="bg-red-600 text-white px-6 py-2 rounded-full font-bold text-lg">
              SOLD
            </span>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-6">
        {/* Title and Category */}
        <div className="mb-3">
          <h3 className="text-xl font-bold text-gray-900 mb-1">
            {item.name}
          </h3>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="capitalize">{item.category}</span>
            {item.location && (
              <>
                <span>•</span>
                <MapPin className="h-4 w-4" />
                <span>{item.location}</span>
              </>
            )}
          </div>
        </div>

        {/* Description */}
        {item.description && (
          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
            {item.description}
          </p>
        )}

        {/* Pricing */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              {item.price ? (
                <>
                  <p className="text-xl font-bold text-red-700">
                    {catalogService.formatPrice(item.price)}
                  </p>
                  {item.category === 'automotive' && (
                    <p className="text-sm text-gray-500">
                      Est. {catalogService.formatPrice(
                        catalogService.calculateMonthlyPayment(item.price)
                      ).replace('R ', '')} p/m
                    </p>
                  )}
                </>
              ) : (
                <p className="text-lg font-semibold text-gray-900">
                  Contact for Price
                </p>
              )}
            </div>
            
            <div className="text-right">
              <p className="font-semibold text-gray-900">
                {item.brand || catalog.botName}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {catalog.whatsappNumber && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onContact('whatsapp', catalog.whatsappNumber);
                }}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </button>
            )}
            
            {catalog.contactNumber && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onContact('phone', catalog.contactNumber);
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Phone className="h-4 w-4" />
                Call
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Item Detail Modal Component
const ItemDetailModal = ({ item, catalog, onClose, onContact }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  const getCategoryIcon = (category) => {
    const icons = {
      'automotive': '🚗',
      'real-estate': '🏠',
      'retail': '🛍️',
      'food': '🍽️',
      'services': '🔧',
      'default': '📦'
    };
    return icons[category] || icons.default;
  };

  const handleShare = async () => {
    const shareData = {
      title: item.name,
      text: `Check out ${item.name}${item.price ? ` - ${catalogService.formatPrice(item.price)}` : ''}`,
      url: window.location.href
    };

    if (navigator.share) {
      await navigator.share(shareData);
    } else {
      navigator.clipboard.writeText(`${shareData.title} - ${shareData.url}`);
      alert('Link copied to clipboard!');
    }
  };

  const images = Array.isArray(item.images) ? item.images : [];
  const primaryImage = item.imageUrl || images[0];
  const displayImage = images.length > 0
    ? (images[currentImageIndex] || primaryImage)
    : primaryImage;

  useEffect(() => {
    setCurrentImageIndex(0);
    setImageLoaded(false);
  }, [item.id, images.length]);

  const handlePrevImage = () => {
    if (images.length < 2) return;
    setImageLoaded(false);
    setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
  };

  const handleNextImage = () => {
    if (images.length < 2) return;
    setImageLoaded(false);
    setCurrentImageIndex(prev => (prev + 1) % images.length);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 lg:p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-100 border-b border-gray-200 p-4 shadow-md
                        flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          {/* Item details */}
          <div className="order-2 sm:order-1 flex items-start gap-3">
            <span className="text-2xl">{getCategoryIcon(item.category)}</span>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{item.name}</h2>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="capitalize">{item.category}</span>
                {item.location && (
                  <>
                    <span>•</span>
                    <MapPin className="h-4 w-4" />
                    <span>{item.location}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="order-1 sm:order-2 flex items-center gap-2 self-end sm:self-auto">
            <button
              onClick={handleShare}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Share"
            >
              <Share2 className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Image */}
            <div>
              <div className="-mx-6 sm:mx-2">
                <div className="relative aspect-square bg-gray-200 rounded-none sm:rounded-xl overflow-hidden mb-4">
                  {displayImage ? (
                    <>
                      <img
                        src={displayImage}
                        alt={item.name}
                        className={`w-full h-full object-cover ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                        onLoad={() => setImageLoaded(true)}
                      />
                      {!imageLoaded && (
                        <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-8xl">
                      {getCategoryIcon(item.category)}
                    </div>
                  )}

                  {images.length > 1 && (
                    <>
                      <button
                        onClick={handlePrevImage}
                        className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 text-gray-700 hover:bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Previous image"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button
                        onClick={handleNextImage}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 text-gray-700 hover:bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Next image"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                      <div className="absolute bottom-3 right-3 bg-black/60 text-white px-2 py-1 rounded-full text-xs backdrop-blur-sm">
                        {currentImageIndex + 1} / {images.length}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* External Links */}
              {catalog.allowExternalLinks && item.externalLinks && item.externalLinks.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-900">Additional Links</h4>
                  {item.externalLinks.map((link, index) => (
                    link.url && link.label && (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                      >
                        <ExternalLink className="h-4 w-4 text-blue-600" />
                        <span className="text-blue-600 font-medium">{link.label}</span>
                      </a>
                    )
                  ))}
                </div>
              )}
            </div>

            {/* Right Column - Details */}
            <div className="space-y-6">
              {/* Price */}
              {item.price && (
                <div>
                  <p className="text-3xl font-bold text-gray-900">
                    {catalogService.formatPrice(item.price)}
                  </p>
                  {item.category === 'automotive' && (
                    <p className="text-lg text-gray-600">
                      Est. R {catalogService.formatPrice(
                        catalogService.calculateMonthlyPayment(item.price)
                      ).replace('R ', '')} p/m
                    </p>
                  )}
                </div>
              )}

              {/* Description */}
              {item.description && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
                  <p className="text-gray-700 leading-relaxed">{item.description}</p>
                </div>
              )}

              {/* Specifications */}
              {item.specs && Object.keys(item.specs).length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Specifications</h4>
                  <div className="grid grid-cols-1 gap-3">
                    {Object.entries(item.specs).map(([key, value]) => (
                      value && (
                        <div key={key} className="flex justify-between py-2 border-b border-gray-100">
                          <span className="font-medium capitalize text-gray-700">{key}:</span>
                          <span className="text-gray-900">{value}</span>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* Tags */}
              {item.tags && item.tags.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {item.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Company Info & Contact */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  {catalog.logoUrl && (
                    <img 
                      src={catalog.logoUrl} 
                      alt="Company Logo" 
                      className="h-10 w-auto object-contain"
                    />
                  )}
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {catalog.companyName || catalog.botName}
                    </h4>
                  </div>
                </div>

                {/* Contact Buttons */}
                <div className="grid grid-cols-1 gap-2">
                  {catalog.whatsappNumber && (
                    <button
                      onClick={() => onContact('whatsapp', catalog.whatsappNumber)}
                      className="bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <MessageCircle className="h-5 w-5" />
                      WhatsApp
                    </button>
                  )}
                  
                  {catalog.contactNumber && (
                    <button
                      onClick={() => onContact('phone', catalog.contactNumber)}
                      className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <Phone className="h-5 w-5" />
                      Call Now
                    </button>
                  )}

                  {catalog.email && (
                    <button
                      onClick={() => onContact('email', catalog.email)}
                      className="bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      📧 Email
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
  </div>
);

// Empty State Component
const EmptyState = ({ searchTerm }) => (
  <div className="text-center py-16">
    <div className="text-6xl mb-4">📦</div>
    <h3 className="text-lg font-medium text-gray-900 mb-2">
      {searchTerm ? 'No items found' : 'No items available'}
    </h3>
    <p className="text-gray-500">
      {searchTerm 
        ? `No items match "${searchTerm}". Try adjusting your search or filters.`
        : 'Items will appear here once they are added to the catalog.'
      }
    </p>
  </div>
);
